import api from './apiService';
import { requireArenaSlug } from '@/utils/arena';

export interface Product {
  id: number;
  name: string;
  price: number;
  buy_price: number;
  rent_price: number;
  category_name: string;
  stock: number;
  sku: string;
  slug: string;
  product_type: string;
  is_active: boolean;
  is_sellable: boolean;
  is_rentable: boolean;
  images: string[];
}

export interface ProductApiResponse {
  success: boolean;
  message: string;
  data: {
    arena_name: string;
    arena_slug: string;
    products: Product[];
  };
}

/**
 * Product service for managing product-related API calls
 */
export class ProductApiService {
  /**
   * Get all products for a specific arena
   */
  static async getProductsByArena(isAdministration?: boolean): Promise<Product[]> {
    try {
      let arenaSlug: string;
      try {
        arenaSlug = requireArenaSlug();
      } catch (error) {
        // Fallback to nikolas-futsal-1 for testing
        console.warn('Arena slug not found, using fallback:', error);
        arenaSlug = 'nikolas-futsal-1';
      }

      let urlProduct = `products/${arenaSlug}`;
      if (isAdministration) {
        urlProduct += '?is-administration=true';
      }

      const response = await api.get<ProductApiResponse>(urlProduct);

      console.log('API Response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch products');
      }

      // Filter only active products and ensure we return an array
      const products = Array.isArray(response.data.data.products)
        ? response.data.data.products.filter((product) => product.is_active !== false)
        : [];
      return products;
    } catch (error: any) {
      console.error('Error fetching products by arena:', error);
      // Return empty array on error to prevent map() issues
      return [];
    }
  }

  /**
   * Search products by name
   */
  static async searchProducts(query: string): Promise<Product[]> {
    try {
      const arenaSlug = requireArenaSlug();

      console.log('Searching products for arena:', arenaSlug, 'query:', query);
      const response = await api.get<ProductApiResponse>(`products/${arenaSlug}`, {
        params: { search: query },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to search products');
      }

      // Filter sellable products and search by name
      const products = response.data.data.products
        .filter((product) => product.is_sellable)
        .filter((product) => product.name.toLowerCase().includes(query.toLowerCase()));
      return Array.isArray(products) ? products : [];
    } catch (error: any) {
      console.error('Error searching products:', error);
      // Return empty array on error to prevent map() issues
      return [];
    }
  }

  /**
   * Update product visibility
   */
  static async updateProductVisibility(productId: number, isActive: boolean): Promise<void> {
    try {
      const arenaSlug = requireArenaSlug();
      const url = `/arenas/${arenaSlug}/product/${productId}/visibility`;
      await api.put(url, { is_active: isActive });
    } catch (error: any) {
      console.error('Failed to update product visibility:', error);
      throw error;
    }
  }

  static async updateFieldVisibility(slug: string, isActive: boolean): Promise<void> {
    try {
      const arenaSlug = requireArenaSlug();
      const url = `/arenas/${arenaSlug}/physical-field/${slug}/visibility`;
      await api.put(url, { is_active: isActive });
    } catch (error: any) {
      console.error('Failed to update product visibility:', error);
      throw error;
    }
  }

  /**
   * Get product details by slug
   */
  static async getProductDetails(productSlug: string): Promise<any> {
    try {
      const arenaSlug = requireArenaSlug();
      const url = `/arenas/${arenaSlug}/product/${productSlug}/detail`;

      const response = await api.get(url);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch product details');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to get product details:', error);
      throw error;
    }
  }

  /**
   * Update product data
   */
  static async updateProduct(productSlug: string, formData: FormData): Promise<any> {
    try {
      const arenaSlug = requireArenaSlug();
      const url = `/arenas/${arenaSlug}/product/${productSlug}/ss`;

      const response = await api.put(url, formData);

      return response.data;
    } catch (error: any) {
      console.error('Failed to update product:', error);
      throw error;
    }
  }

  static async deleteProduct(productSlug: string): Promise<void> {
    try {
      const arenaSlug = requireArenaSlug();
      const url = `/arenas/${arenaSlug}/product/${productSlug}`;
      await api.delete(url);
    } catch (error: any) {
      console.error('Failed to delete product:', error);
      throw error;
    }
  }

  /**
   * Get product image URL with fallback
   */
  static getProductImageUrl(product: Product): string {
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      // If image is a full URL, return as is
      if (firstImage.startsWith('http')) {
        return firstImage;
      }
      // If image is a relative path, construct full URL
      return `${process.env.VITE_API_BASE_URL || 'http://localhost:8080'}/uploads/${firstImage}`;
    }
    // Fallback to default image
    return '/sporto-logo.png';
  }
}

export default ProductApiService;
